import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import rehypeParse from "rehype-parse";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import { remark } from "remark";
import { unified } from "unified";

export function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function splitCsv(s: string | undefined): string[] {
  if (!s) return [];
  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

const schema = {
  ...defaultSchema,
  strip: [...(defaultSchema.strip ?? []), "style"],
  attributes: {
    ...defaultSchema.attributes,
    "*": [...(defaultSchema.attributes?.["*"] || []), "className"],
    img: [
      ...(defaultSchema.attributes?.img || []),
      "src",
      "alt",
      "title",
      "loading",
      "width",
      "height",
    ],
    a: [...(defaultSchema.attributes?.a || []), "target", "rel"],
  },
};

function looksLikeHtml(content: string): boolean {
  return /<[a-z][\s\S]*>/i.test(content);
}

/** Pasted HTML from the admin is sanitized like markdown output: scripts/styles and
 * disallowed tags (e.g. footer, header, iframe) are removed so they cannot mimic the site layout.
 */
async function sanitizeHtmlFragment(html: string): Promise<string> {
  const file = await unified()
    .use(rehypeParse, { fragment: true })
    .use(rehypeSanitize, schema)
    .use(rehypeStringify)
    .process(html);
  return String(file).trim();
}

export async function renderCmsContent(content?: string | null): Promise<string> {
  const raw = (content ?? "").trim();
  if (!raw) return "";
  if (looksLikeHtml(raw)) {
    return sanitizeHtmlFragment(raw);
  }

  const file = await remark()
    .use(remarkGfm)
    .use(remarkBreaks)
    .use(remarkRehype)
    .use(rehypeSanitize, schema)
    .use(rehypeStringify)
    .process(raw);

  return String(file);
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
